/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { alias } from '@ember/object/computed';
import { isEmpty, isBlank } from '@ember/utils';

import { Editor } from 'cio/services/editor';
import TemplateModel from 'cio/models/template';
import AnalyticsService from 'cio/services/analytics';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';
import { EmailPreview, HeaderError, InAppPreview } from 'cio/utils/previews';
import { ComposerActionTypeName } from 'cio/utils/composer';
import { ComposerTab } from 'cio/components/action-composer-v2';

interface VisualEditorSignature {
  Args: {
    editableEntity: ComposerEditableEntity;
    composerTab: ComposerTab;
    onRollback: () => unknown;
    onSave: () => Promise<unknown>;
    onTabChange?: (tab: ComposerTab) => unknown;
    hasReadOnlyPermission: boolean;
    type: ComposerActionTypeName;
  };
  Element: HTMLDivElement;
}

export default class VisualEditor extends Component<VisualEditorSignature> {
  @service analytics!: AnalyticsService;

  @tracked originalFromIdentity?: TemplateModel['fromIdentity'];
  @tracked originalReplyToIdentity?: TemplateModel['replyToIdentity'];

  @tracked previousComposerTab: ComposerTab | null = null;
  @tracked composerTab: ComposerTab = this.args.composerTab ?? ComposerTab.Editor;

  @tracked preview: EmailPreview | InAppPreview | null = null;

  @alias('hasHeaderErrors') isInvalid!: boolean;

  get isInApp() {
    return this.args.editableEntity.isInApp;
  }

  get isEmail() {
    return this.args.editableEntity.isEmail;
  }

  @computed('args.hasReadOnlyPermission', 'isDirty', 'isInvalid')
  get saveDisabled() {
    return this.args.hasReadOnlyPermission || !this.isDirty || this.isInvalid;
  }

  @computed('args.hasReadOnlyPermission', 'hasHeaderErrors', 'isDirty')
  get saveDisabledTooltipContent() {
    if (this.hasHeaderErrors) {
      return this.saveTooltipContent;
    }

    if (!this.isDirty) {
      return 'No changes to save!';
    }

    if (this.args.hasReadOnlyPermission) {
      return "You don't have write permissions.";
    }

    return '';
  }

  @computed(
    'args.editableEntity.template.{fromIdentityId,replyToIdentity.id,hasDirtyAttributes,body,bodyJson}',
    'originalFromIdentity.id',
    'originalReplyToIdentity.id',
  )
  get isDirty() {
    let { template } = this.args.editableEntity;
    let isFromDirty = (this.originalFromIdentity?.id ?? null) !== template?.fromIdentityId;
    let isReplyToDirty = (this.originalReplyToIdentity?.id ?? null) !== template?.belongsTo('replyToIdentity').id();
    let isTemplateDirty = template?.get('hasDirtyAttributes') ?? false;

    let areRelationshipsDirty = isFromDirty || isReplyToDirty;
    return areRelationshipsDirty || isTemplateDirty;
  }

  get hasHeaderErrors() {
    if (!this.args.editableEntity.isEmail) {
      return false;
    }

    let errors: HeaderError[] = (this.preview as EmailPreview)?.errors?.headers ?? [];
    return errors.some((error) => !isEmpty(error.name) || !isEmpty(error.value));
  }

  get saveTooltipContent() {
    if (this.hasHeaderErrors) {
      return 'Please fix any errors in your email headers to save your message.';
    }

    return null;
  }

  constructor(owner: unknown, args: VisualEditorSignature['Args']) {
    super(owner, args);
    this.cacheOriginalRelationships();
  }

  @action
  rollback() {
    this.args.editableEntity.template?.rollbackAttributes();
    this.args.editableEntity.template?.setProperties({
      fromIdentity: this.originalFromIdentity,
      replyToIdentity: this.originalReplyToIdentity,
    });

    return this.args.onRollback();
  }

  @action
  save() {
    this.removeEmptyHeaders();
    let { template } = this.args.editableEntity;

    if (isEmpty(template?.editor)) {
      template?.set('editor', Editor.VISUAL);
    }

    if (template?.layout != null) {
      template?.set('layout', null);
    }

    let promise = this.args.onSave();

    promise.then(
      () => {
        this.cacheOriginalRelationships();
      },
      () => {},
    );

    return promise;
  }

  removeEmptyHeaders() {
    let { template } = this.args.editableEntity;
    if (template == null) {
      return;
    }

    let toRemove = template.headers.filter((header) => isBlank(header.name) && isBlank(header.value));
    template.headers.removeObjects(toRemove);
  }

  cacheOriginalRelationships() {
    if (!this.args.editableEntity.isEmail) {
      return;
    }

    this.originalFromIdentity = this.args.editableEntity.template?.get('fromIdentity');
    this.originalReplyToIdentity = this.args.editableEntity.template?.get('replyToIdentity');
  }

  @action
  onTemplateSwitch() {
    this.cacheOriginalRelationships();
  }

  @action
  onComposerTabChange(tab: ComposerTab) {
    this.previousComposerTab = this.args.composerTab;
    this.composerTab = tab;
    this.args.onTabChange?.(tab);
  }

  @action
  onPreviewChange(preview: EmailPreview | InAppPreview | null) {
    this.preview = preview;
  }
}
